import {Button, Group, Paper, Stack, Text} from '@mantine/core';
import {FC, useCallback, useEffect, useMemo} from 'react';
import {SubmitHandler, useFormContext} from 'react-hook-form';
import {Link, useNavigate} from 'react-router-dom';
import {useTypedParams} from 'react-router-typesafe-routes/dom';
import {useAsync, useAsyncFn} from 'react-use';
import {CodesService} from '../api/generated/CodesService';
import {EmailEvaluationsService} from '../api/generated/EmailEvaluationsService';
import {PartnerSitesService} from '../api/generated/PartnerSitesService';
import {UsersService} from '../api/generated/UsersService';
import {FilterOnOptions} from '../api/generated/enums';
import {AsyncStateContainer} from '../components/async-state-container';
import {BasicPage} from '../components/basic-page';
import {DeleteButton} from '../components/delete-button';
import {BreadcrumbPart} from '../components/page-title';
import {createForm} from '../forms';
import {useNotification} from '../hooks/use-notifications';
import {useOptions} from '../hooks/use-options';
import {ROUTES} from '../routes';
import {EmailEvaluationSchema, emailEvaluationSchema} from './schemas';
import {useRequireAuthorization} from '../auth/require-authorization';
import {useUser} from '../auth/use-auth';

const breadcrumbs: BreadcrumbPart[] = [
  {title: 'Email Evaluations', href: ROUTES.ADMIN.EMAIL_EVALUATIONS.INDEX.path},
  {title: 'Create'},
];

const Form = createForm<EmailEvaluationSchema>();

export const EmailEvaluationCreate = () => {
  const navigate = useNavigate();
  const notifications = useNotification();

  const onSubmit = useCallback<SubmitHandler<EmailEvaluationSchema>>(
    async (value) => {
      const response = await EmailEvaluationsService.create({
        body: value,
      });

      if (response.hasErrors) {
        return response;
      }

      notifications.success('Email Evaluation Created');
      navigate(ROUTES.ADMIN.EMAIL_EVALUATIONS.INDEX.path);
    },
    [navigate, notifications],
  );

  return (
    <BasicPage title={breadcrumbs}>
      <Form schema={emailEvaluationSchema} onSubmit={onSubmit}>
        <Form.Container>
          <FormFields />
          <Group mt={24} justify={'flex-end'}>
            <Button
              type='button'
              variant='outline'
              component={Link}
              to={ROUTES.ADMIN.EMAIL_EVALUATIONS.INDEX.path}
            >
              Cancel
            </Button>
            <Form.Submit>Create Email Evaluation</Form.Submit>
          </Group>
        </Form.Container>
      </Form>
    </BasicPage>
  );
};

export const EmailEvaluationUpdate: FC = () => {
  const navigate = useNavigate();
  const notifications = useNotification();
  const {id} = useTypedParams(ROUTES.ADMIN.EMAIL_EVALUATIONS.DETAIL);

  const breadcrumbs: BreadcrumbPart[] = useMemo(
    () => [
      {
        title: 'Email Evaluations',
        href: ROUTES.ADMIN.EMAIL_EVALUATIONS.INDEX.path,
      },
      {title: `${id}`},
    ],
    [id],
  );

  const fetchEmailEvaluation = useAsync(async () => {
    const {data} = await EmailEvaluationsService.getById({
      id,
    });
    return data;
  }, [id]);

  const emailEvaluation = fetchEmailEvaluation.value ?? undefined;

  const onSubmit = useCallback<SubmitHandler<EmailEvaluationSchema>>(
    async (value) => {
      const response = await EmailEvaluationsService.update({
        id,
        body: value,
      });

      if (response.hasErrors) {
        return response;
      }

      notifications.success('Email Evaluation Updated');
      navigate(ROUTES.ADMIN.EMAIL_EVALUATIONS.INDEX.path);
    },
    [id, navigate, notifications],
  );

  const [deleteEmailEvaluationState, deleteEmailEvaluation] =
    useAsyncFn(async () => {
      const response = await EmailEvaluationsService.deleteById({id});

      if (response.hasErrors) {
        notifications.error('Unable to delete Email Evaluation');
        return;
      }

      notifications.success('Email Evaluation successfully deleted');
      navigate(ROUTES.ADMIN.EMAIL_EVALUATIONS.INDEX.path);
    }, [id, navigate, notifications]);

  return (
    <BasicPage title={breadcrumbs}>
      <AsyncStateContainer {...fetchEmailEvaluation}>
        <Stack gap='xl'>
          <Paper withBorder shadow='sm'>
            <Form
              schema={emailEvaluationSchema}
              onSubmit={onSubmit}
              defaultValues={emailEvaluation}
            >
              <Form.Container withBorder={false}>
                <FormFields />
                <Group mt={24} justify='space-between'>
                  <DeleteButton
                    onConfirm={deleteEmailEvaluation}
                    buttonLabel='Delete Email Evaluation'
                    confirmButtonLabel='Delete Email Evaluation'
                    loading={deleteEmailEvaluationState.loading}
                    confirmTitle={
                      <Text fw={600}>
                        Are you sure you want to delete this Email Evaluation?
                      </Text>
                    }
                  />
                  <Group>
                    <Button
                      type='button'
                      variant='outline'
                      component={Link}
                      to={ROUTES.ADMIN.EMAIL_EVALUATIONS.INDEX.path}
                    >
                      Cancel
                    </Button>
                    <Form.Submit>Update Email Evaluation</Form.Submit>
                  </Group>
                </Group>
              </Form.Container>
            </Form>
          </Paper>
        </Stack>
      </AsyncStateContainer>
    </BasicPage>
  );
};

const FormFields: FC = () => {
  const {setValue} = useFormContext<EmailEvaluationSchema>();
  const fetchPartnerSiteOptions = useOptions(PartnerSitesService.getOptions);
  const fetchUserOptions = useOptions(UsersService.getOptions);
  const fetchLevelOfServiceOptions = useOptions(
    CodesService.getLevelOfServiceCodeOptions,
  );

  const {id: userId} = useUser();

  const allowFilterOnUser = useRequireAuthorization({
    role: ['Global Admin', 'Partner Site Manager'],
  });

  const partnerSiteOptions = fetchPartnerSiteOptions.value ?? [];
  const userOptions = fetchUserOptions.value ?? [];
  const levelOfServiceOptions = fetchLevelOfServiceOptions.value ?? [];

  useEffect(() => {
    if (!allowFilterOnUser) {
      setValue('filterOnEnteredByUserId', userId);
    }
  }, [setValue, userId, allowFilterOnUser]);

  return (
    <Stack gap='xs'>
      <Form.Row mt={14}>
        <Form.TextInput name='email' label='Email' />
        <Form.Select
          label='Partner Site'
          name='partnerSiteId'
          loading={fetchPartnerSiteOptions.loading}
          data={partnerSiteOptions}
          placeholder='All'
        />
      </Form.Row>
      <Form.Row mt={14}>
        <Form.Select
          label='Filter on Entered by User'
          name='filterOnEnteredByUserId'
          loading={fetchUserOptions.loading}
          clearable={allowFilterOnUser}
          disabled={!allowFilterOnUser}
          data={userOptions}
          placeholder='All'
        />
        <Form.Select
          label='Filter on Level of Service'
          name='filterOnLevelOfServiceId'
          loading={fetchLevelOfServiceOptions.loading}
          data={levelOfServiceOptions}
          placeholder='All'
          clearable
        />
      </Form.Row>
      <Form.Row mt={14}>
        <Form.Select
          name='filterOnCanBeRepaired'
          enumObject={FilterOnOptions}
          label='Filter on Can Be Repaired'
          placeholder='All'
          clearable
        />
        <Form.TextInput
          name='filterOnClientNamed'
          label='Filter on Client Named'
        />
      </Form.Row>
    </Stack>
  );
};